/**
 * Chorus API Client
 * All AI requests go through the Cloudflare Worker — no client-side API keys.
 * The Worker holds secrets and proxies to providers.
 */

// Worker API base URL — auto-detect from current host or fallback
const API_BASE = (() => {
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    // Test environment
    if (host.includes('chorus-test')) return 'https://chorus-api-test.ets-coding2020.workers.dev';
    // Production
    if (host.includes('chorus')) return 'https://chorus-api.ets-coding2020.workers.dev';
  }
  // Local dev → use local worker or deployed test
  return 'https://chorus-api-test.ets-coding2020.workers.dev';
})();

/**
 * Get auth headers with Supabase token
 */
function getAuthHeaders() {
  const headers = { 'Content-Type': 'application/json' };
  try {
    const session = JSON.parse(localStorage.getItem('chorus_session') || '{}');
    if (session.access_token) {
      headers['Authorization'] = `Bearer ${session.access_token}`;
    }
  } catch {}
  return headers;
}

/**
 * Fetch available models from the Worker
 * @returns {Promise<{providers: Array}>}
 */
export async function fetchModels() {
  try {
    const res = await fetch(`${API_BASE}/api/models`);
    if (!res.ok) throw new Error(`Failed to fetch models: ${res.status}`);
    return await res.json();
  } catch (error) {
    console.error('Failed to fetch models:', error);
    return { providers: [] };
  }
}

/**
 * Stream a chat completion through the Worker
 * @param {Object} params
 * @param {string} params.provider - Provider id (openai, claude, deepseek, google)
 * @param {string} params.model - Model id
 * @param {Array<{role: string, content: string}>} params.messages
 * @param {number} [params.temperature=0.7]
 * @param {number} [params.maxTokens=4096]
 * @param {AbortSignal} [params.signal]
 * @yields {{ type: 'text' | 'done' | 'error', content: string }}
 */
export async function* streamChat({ provider, model, messages, temperature = 0.7, maxTokens = 4096, signal, ragContext, memoryContext, searchContext }) {
  try {
    const body = {
      provider,
      model,
      messages,
      temperature,
      maxTokens,
      stream: true,
    };
    if (ragContext) body.ragContext = ragContext;
    if (memoryContext) body.memoryContext = memoryContext;
    if (searchContext) body.searchContext = searchContext;

    const response = await fetch(`${API_BASE}/api/chat`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(body),
      signal,
    });

    if (!response.ok) {
      let errorMsg;
      try {
        const err = await response.json();
        errorMsg = err.error || `HTTP ${response.status}`;
      } catch {
        errorMsg = `HTTP ${response.status}: ${response.statusText}`;
      }
      yield { type: 'error', content: errorMsg };
      return;
    }

    // Parse SSE stream
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6);
            if (data === '[DONE]') {
              yield { type: 'done', content: '' };
              return;
            }

            try {
              const parsed = JSON.parse(data);

              // OpenAI format
              if (parsed.choices?.[0]?.delta?.content) {
                yield { type: 'text', content: parsed.choices[0].delta.content };
              }
              if (parsed.choices?.[0]?.finish_reason === 'stop') {
                yield { type: 'done', content: '' };
                return;
              }

              // Claude format
              if (parsed.type === 'content_block_delta' && parsed.delta?.text) {
                yield { type: 'text', content: parsed.delta.text };
              }
              if (parsed.type === 'message_stop') {
                yield { type: 'done', content: '' };
                return;
              }
            } catch {
              // Skip malformed chunks
            }
          }
        }
      }
    } finally {
      reader.releaseLock();
    }

    yield { type: 'done', content: '' };
  } catch (error) {
    if (error.name === 'AbortError') {
      yield { type: 'done', content: '' };
    } else {
      yield { type: 'error', content: error.message };
    }
  }
}

/**
 * Check API health
 */
export async function checkHealth() {
  try {
    const res = await fetch(`${API_BASE}/api/health`, { signal: AbortSignal.timeout(3000) });
    return res.ok;
  } catch {
    return false;
  }
}

// ─────────────────────────────────────────
// MISSION CONTROL API
// ─────────────────────────────────────────

/**
 * Fetch all agent sessions from OpenClaw gateways
 */
export async function fetchMissionAgents() {
  try {
    const res = await fetch(`${API_BASE}/api/mission/agents`, {
      headers: getAuthHeaders(),
    });
    if (!res.ok) throw new Error(`Failed to fetch agents: ${res.status}`);
    return await res.json();
  } catch (error) {
    console.error('Failed to fetch mission agents:', error);
    return { agents: [], error: error.message };
  }
}

/**
/**
 * Fetch agent chat history from transcript (for agent tab chat view)
 */
export async function fetchAgentHistory(tabId, limit = 500, offset = 0) {
  try {
    const res = await fetch(`${API_BASE}/api/agent/history`, {
      method: 'POST',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ tabId, limit, offset, _t: Date.now() }),
    });
    if (!res.ok) throw new Error(`Failed to fetch agent history: ${res.status}`);
    return await res.json();
  } catch (error) {
    console.error('Failed to fetch agent history:', error);
    return { messages: [], error: error.message };
  }
}

/**
 * Fetch all cron jobs
 */
export async function fetchCronJobs() {
  try {
    const res = await fetch(`${API_BASE}/api/mission/cron`, {
      headers: getAuthHeaders(),
    });
    if (!res.ok) throw new Error(`Failed to fetch cron jobs: ${res.status}`);
    return await res.json();
  } catch (error) {
    console.error('Failed to fetch cron jobs:', error);
    return { jobs: [], error: error.message };
  }
}

/**
 * Fetch cron job run history
 */
export async function fetchCronRuns(jobId) {
  try {
    const res = await fetch(`${API_BASE}/api/mission/cron/${jobId}/runs`, {
      headers: getAuthHeaders(),
    });
    if (!res.ok) throw new Error(`Failed to fetch cron runs: ${res.status}`);
    return await res.json();
  } catch (error) {
    console.error('Failed to fetch cron runs:', error);
    return { runs: [], error: error.message };
  }
}

/**
 * Fetch agent workspace files
 */
export async function fetchAgentFiles(agentId) {
  try {
    const res = await fetch(`${API_BASE}/api/mission/agent/${agentId}/files`, {
      headers: getAuthHeaders(),
    });
    if (!res.ok) throw new Error(`Failed to fetch agent files: ${res.status}`);
    return await res.json();
  } catch (error) {
    console.error('Failed to fetch agent files:', error);
    return { files: '', error: error.message };
  }
}

/**
 * Fetch a specific agent file content
 */
export async function fetchAgentFile(agentId, filePath) {
  try {
    const res = await fetch(`${API_BASE}/api/mission/agent/${agentId}/file?path=${encodeURIComponent(filePath)}`, {
      headers: getAuthHeaders(),
    });
    if (!res.ok) throw new Error(`Failed to fetch file: ${res.status}`);
    return await res.json();
  } catch (error) {
    console.error('Failed to fetch file:', error);
    return { content: '', error: error.message };
  }
}

/**
 * Send a message to an agent's session
 */
export async function sendAgentMessage(sessionKey, message, environment) {
  try {
    const res = await fetch(`${API_BASE}/api/mission/agent/send`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ sessionKey, message, environment }),
    });
    if (!res.ok) throw new Error(`Failed to send message: ${res.status}`);
    return await res.json();
  } catch (error) {
    console.error('Failed to send agent message:', error);
    return { ok: false, error: error.message };
  }
}

/**
 * Ping an agent's gateway to check latency
 */
export async function pingAgent(environment) {
  try {
    const res = await fetch(`${API_BASE}/api/mission/agent/ping`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ environment }),
    });
    return await res.json();
  } catch (error) {
    console.error('Failed to ping agent:', error);
    return { ok: false, status: 'offline', latency: -1, error: error.message };
  }
}

/**
 * Fetch full conversation history for replay
 */
export async function fetchConversationReplay(sessionKey, environment, limit = 50, offset = 0) {
  try {
    const res = await fetch(`${API_BASE}/api/mission/history/${encodeURIComponent(sessionKey)}?env=${environment}&limit=${limit}&offset=${offset}`, {
      headers: getAuthHeaders(),
    });
    if (!res.ok) throw new Error(`Failed to fetch history: ${res.status}`);
    return await res.json();
  } catch (error) {
    console.error('Failed to fetch conversation replay:', error);
    return { messages: [], error: error.message };
  }
}

/**
 * Fetch full conversation transcript from .jsonl files (bypasses session compaction)
 */
export async function fetchTranscript(agentId, transcriptPath, environment, limit = 500, offset = 0) {
  try {
    const res = await fetch(`${API_BASE}/api/mission/transcript`, {
      method: 'POST',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ agentId, transcriptPath, environment, limit, offset }),
    });
    if (!res.ok) throw new Error(`Failed to fetch transcript: ${res.status}`);
    return await res.json();
  } catch (error) {
    console.error('Failed to fetch transcript:', error);
    return { messages: [], error: error.message };
  }
}

/**
 * Send a command to an agent's gateway
 */
export async function sendAgentCommand(command, environment, params = {}) {
  try {
    const res = await fetch(`${API_BASE}/api/mission/command`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ command, environment, params }),
    });
    return await res.json();
  } catch (error) {
    console.error('Failed to send command:', error);
    return { ok: false, error: error.message };
  }
}

export async function readAgentFile(filePath, environment) {
  try {
    const res = await fetch(`${API_BASE}/api/mission/file/read`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ path: filePath, environment }),
    });
    return await res.json();
  } catch (error) {
    console.error('Failed to read file:', error);
    return { ok: false, error: error.message };
  }
}

export { API_BASE };
